import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import axios from "axios";
import apiUrls from "../../api";
import {
  fetchReports,
  deleteReport,
  updateReport,
  reportsSelector,
  reportsIsLoadingSelector,
  reportsLastPreviewSelector,
  clearLastPreview,
} from "../../features/reports/state/reportsSlice";
import type { ScheduledEmailReport } from "../../features/reports/types";
import RowActionsMenu from "../../features/reports/components/RowActionsMenu";
import StatusBadge from "../../features/reports/components/StatusBadge";
import EnabledBadge from "../../features/reports/components/EnabledBadge";
import ModalConfirmation from "../../common/components/Modal/ModalConfirmation";
import {
  openConfirmationModal,
  closeConfirmationModal,
} from "../../common/state/slice/modal/modalSlice";
import {
  SearchAndSort,
  SkeletonTable,
  EmptyState,
  Pagination,
} from "../../features/statsNew/components";

// Same ID as the one in ReportsFormView so closing/opening across the two
// pages uses one and the same redux modal slot. The redux slice tracks
// confirmation modals by id, so the slot is per-instance regardless.
const DELETE_REPORT_MODAL_ID = "delete-scheduled-email-report-confirmation";

const formatDateTime = (iso: string | null) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatPeriod = (start?: string, end?: string): string => {
  if (!start || !end) return "";
  try {
    const s = new Date(start).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const e = new Date(end).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return `${s} → ${e}`;
  } catch {
    return "";
  }
};

type SortColumn = "name" | "scope" | "cadence" | "recipients" | "lastRun" | "status";
type SortDirection = "asc" | "desc";

const ReportsListView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const reports = useAppSelector(reportsSelector);
  const isLoading = useAppSelector(reportsIsLoadingSelector);
  const lastPreview = useAppSelector(reportsLastPreviewSelector);

  // Holds which report the user just asked to delete. The modal itself is
  // open/closed via the redux modal slice (same pattern as parcours/RAG
  // deletion), but the id-to-delete stays in local state because the modal
  // component doesn't carry payload between open and confirm.
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("lastRun");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [pgNameById, setPgNameById] = useState<Record<number, string>>({});
  const [projectInfoById, setProjectInfoById] = useState<
    Record<number, { name: string; pgName: string }>
  >({});
  const [expandedRecipients, setExpandedRecipients] = useState<Set<number>>(new Set());

  useEffect(() => {
    void dispatch(fetchReports());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await axios.get(apiUrls.stats.getAllSessionsByProjectGroup);
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
        const pgMap: Record<number, string> = {};
        const projMap: Record<number, { name: string; pgName: string }> = {};
        for (const pg of raw) {
          pgMap[pg.project_group_id] = pg.project_group_name;
          if (Array.isArray(pg.projects)) {
            for (const p of pg.projects) {
              projMap[p.project_id] = {
                name: p.project_name,
                pgName: pg.project_group_name,
              };
            }
          }
        }
        setPgNameById(pgMap);
        setProjectInfoById(projMap);
      } catch {
        // Names will fall back to "#id" — non-blocking
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [search, sortColumn, sortDirection]);

  const filteredAndSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q ? reports.filter((x) => x.name.toLowerCase().includes(q)) : reports.slice();
    const dir = sortDirection === "asc" ? 1 : -1;
    filtered.sort((a, b) => {
      switch (sortColumn) {
        case "name":
          return a.name.localeCompare(b.name) * dir;
        case "scope":
          return a.scope_type.localeCompare(b.scope_type) * dir;
        case "cadence":
          return a.cadence.localeCompare(b.cadence) * dir;
        case "recipients":
          return (a.recipients.length - b.recipients.length) * dir;
        case "lastRun": {
          const ta = a.last_run_at ? new Date(a.last_run_at).getTime() : 0;
          const tb = b.last_run_at ? new Date(b.last_run_at).getTime() : 0;
          return (ta - tb) * dir;
        }
        case "status":
          return (a.last_run_status ?? "").localeCompare(b.last_run_status ?? "") * dir;
        default:
          return 0;
      }
    });
    return filtered;
  }, [reports, search, sortColumn, sortDirection]);

  const paginated = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredAndSorted.slice(start, start + pageSize);
  }, [filteredAndSorted, currentPage, pageSize]);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    return sortDirection === "asc" ? (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 text-green-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 text-green-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const handleToggleEnabled = (report: ScheduledEmailReport) => {
    void dispatch(updateReport({ id: report.id, input: { enabled: !report.enabled } }));
  };

  const handleDeleteRequest = (id: number) => {
    setPendingDeleteId(id);
    dispatch(openConfirmationModal(DELETE_REPORT_MODAL_ID));
  };

  const handleDeleteConfirm = () => {
    if (pendingDeleteId === null) return;
    void dispatch(deleteReport(pendingDeleteId));
    setPendingDeleteId(null);
    dispatch(closeConfirmationModal(DELETE_REPORT_MODAL_ID));
  };

  // Skeleton on initial load
  if (isLoading && reports.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 flex items-center justify-between gap-4 mb-4">
          <SearchAndSort
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder={t("pages.statistics.reports.searchPlaceholder")}
          />
        </div>
        <SkeletonTable rows={8} columns={6} />
      </div>
    );
  }

  const renderEmpty = () => {
    if (search) {
      return (
        <EmptyState
          icon="search"
          title={t("statsNew.emptyState.noSearchResults")}
          description={t("statsNew.emptyState.noSearchResultsDescription")}
        />
      );
    }
    return (
      <EmptyState
        icon="mail"
        title={t("pages.statistics.reports.empty.title")}
        description={t("pages.statistics.reports.empty.body")}
        action={{
          label: t("pages.statistics.reports.empty.cta"),
          onClick: () => navigate("/statistics/reports/new"),
        }}
      />
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search + New action */}
      <div className="flex-shrink-0 flex items-center justify-between gap-4 mb-4">
        <SearchAndSort
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder={t("pages.statistics.reports.searchPlaceholder")}
        />
        <button
          type="button"
          onClick={() => navigate("/statistics/reports/new")}
          className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t("pages.statistics.reports.new")}
        </button>
      </div>

      {/* Last preview banner */}
      {lastPreview && (
        <div
          className={`mb-3 px-4 py-2 text-sm rounded-lg flex items-center justify-between flex-shrink-0 ${
            lastPreview.sent
              ? "bg-green-50 text-green-700"
              : lastPreview.dryRun
              ? "bg-blue-50 text-blue-700"
              : "bg-red-50 text-red-700"
          }`}
        >
          <span>
            {lastPreview.sent
              ? lastPreview.periodStart && lastPreview.periodEnd
                ? t("pages.statistics.reports.preview.successSentWithPeriod", {
                    recipient: lastPreview.recipient,
                    period: formatPeriod(lastPreview.periodStart, lastPreview.periodEnd),
                  })
                : t("pages.statistics.reports.preview.successSent", {
                    recipient: lastPreview.recipient,
                  })
              : lastPreview.dryRun
              ? t("pages.statistics.reports.preview.dryRun", {
                  reason: lastPreview.reason ?? "",
                })
              : t("pages.statistics.reports.preview.error")}
          </span>
          <button
            onClick={() => dispatch(clearLastPreview())}
            className="ml-2 text-current opacity-60 hover:opacity-100"
          >
            ×
          </button>
        </div>
      )}

      {/* Table / empty */}
      {filteredAndSorted.length === 0 ? (
        renderEmpty()
      ) : (
        <div className="flex-1 overflow-y-auto min-h-0 bg-white rounded-xl border border-gray-200">
          <table className="stats-table">
            <thead>
              <tr>
                <th className="sortable cursor-pointer" onClick={() => handleSort("name")}>
                  <div className="flex items-center gap-1">
                    {t("pages.statistics.reports.columns.name")}
                    {getSortIcon("name")}
                  </div>
                </th>
                <th className="sortable cursor-pointer" onClick={() => handleSort("scope")}>
                  <div className="flex items-center gap-1">
                    {t("pages.statistics.reports.columns.scope")}
                    {getSortIcon("scope")}
                  </div>
                </th>
                <th className="sortable cursor-pointer" onClick={() => handleSort("cadence")}>
                  <div className="flex items-center gap-1">
                    {t("pages.statistics.reports.columns.cadence")}
                    {getSortIcon("cadence")}
                  </div>
                </th>
                <th className="sortable cursor-pointer" onClick={() => handleSort("recipients")}>
                  <div className="flex items-center gap-1">
                    {t("pages.statistics.reports.columns.recipients")}
                    {getSortIcon("recipients")}
                  </div>
                </th>
                <th className="sortable cursor-pointer" onClick={() => handleSort("lastRun")}>
                  <div className="flex items-center gap-1">
                    {t("pages.statistics.reports.columns.lastRun")}
                    {getSortIcon("lastRun")}
                  </div>
                </th>
                <th className="sortable cursor-pointer" onClick={() => handleSort("status")}>
                  <div className="flex items-center gap-1">
                    {t("pages.statistics.reports.columns.status")}
                    {getSortIcon("status")}
                  </div>
                </th>
                <th className="w-12" />
              </tr>
            </thead>
            <tbody>
              {paginated.map((r) => (
                <tr
                  key={r.id}
                  className="clickable"
                  onClick={() => navigate(`/statistics/reports/${r.id}/edit`)}
                >
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{r.name}</span>
                      <EnabledBadge enabled={r.enabled} />
                    </div>
                  </td>
                  <td>
                    {(() => {
                      if (r.scope_type === "project_group") {
                        const name = pgNameById[r.scope_id];
                        return name ? (
                          <span className="text-gray-900">{name}</span>
                        ) : (
                          <>
                            {t(`pages.statistics.reports.scopeShort.${r.scope_type}`)}{" "}
                            <span className="text-gray-400">#{r.scope_id}</span>
                          </>
                        );
                      }
                      const info = projectInfoById[r.scope_id];
                      if (info) {
                        return (
                          <div className="flex flex-col">
                            <span className="text-gray-900">{info.pgName}</span>
                            <span className="text-xs italic text-gray-500">{info.name}</span>
                          </div>
                        );
                      }
                      return (
                        <>
                          {t(`pages.statistics.reports.scopeShort.${r.scope_type}`)}{" "}
                          <span className="text-gray-400">#{r.scope_id}</span>
                        </>
                      );
                    })()}
                  </td>
                  <td>{t(`pages.statistics.reports.cadence.${r.cadence}`)}</td>
                  <td>
                    {(() => {
                      const all = r.recipients ?? [];
                      const expanded = expandedRecipients.has(r.id);
                      const visibleCount = expanded ? all.length : 2;
                      const visible = all.slice(0, visibleCount);
                      const remaining = all.length - visible.length;
                      return (
                        <div className="flex flex-col gap-0.5">
                          {visible.map((email) => (
                            <span key={email} className="text-sm text-gray-700">
                              {email}
                            </span>
                          ))}
                          {remaining > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedRecipients((prev) => {
                                  const next = new Set(prev);
                                  next.add(r.id);
                                  return next;
                                });
                              }}
                              className="self-start text-xs text-green-700 hover:underline"
                            >
                              {t("pages.statistics.reports.recipientsMore", {
                                count: remaining,
                              })}
                            </button>
                          )}
                          {expanded && all.length > 2 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedRecipients((prev) => {
                                  const next = new Set(prev);
                                  next.delete(r.id);
                                  return next;
                                });
                              }}
                              className="self-start text-xs text-gray-500 hover:underline"
                            >
                              {t("general.collapse")}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td>{formatDateTime(r.last_run_at)}</td>
                  <td>
                    <StatusBadge status={r.last_run_status} />
                  </td>
                  <td className="text-right" onClick={(e) => e.stopPropagation()}>
                    <RowActionsMenu
                      enabled={r.enabled}
                      onToggleEnabled={() => handleToggleEnabled(r)}
                      onEdit={() => navigate(`/statistics/reports/${r.id}/edit`)}
                      onDelete={() => handleDeleteRequest(r.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {filteredAndSorted.length > 0 && (
        <div className="flex-shrink-0 mt-auto">
          <Pagination
            currentPage={currentPage}
            totalItems={filteredAndSorted.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {/* Delete confirmation — same `ModalConfirmation` used app-wide for
          destructive-but-routine actions (parcours deletion, RAG doc unassign,
          etc.). The reserved type-to-confirm `ModalConfirmDangerousAction` is
          intentionally NOT used here: a scheduled mail config is recreatable
          in 30 seconds, the friction would be disproportionate. */}
      <ModalConfirmation
        id={DELETE_REPORT_MODAL_ID}
        title={t("pages.statistics.reports.deleteConfirm.title")}
        text={t("pages.statistics.reports.deleteConfirm.body")}
        submitButtonText={t("pages.statistics.reports.actions.delete")}
        onClickSubmit={handleDeleteConfirm}
        onClickCancel={() => setPendingDeleteId(null)}
      />
    </div>
  );
};

export default ReportsListView;
